import styled from 'styled-components';

import { OnboardingState } from '../../../../../types/OnboardingState';
import { SupplierOnboardingRequestMessage } from '../../../../../types/SupplierOnboardingRequestMessage';
import { Tag } from '../../../../cdl/Tag/Tag';
import { translate, TranslationKeyEn } from '../../../../common/helpers/translate.helper';
import { useRole } from '../../../../common/hooks/useRole';
import { IconSparkle } from '../../../../common/icons/cdl/Sparkle';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import { OfferModel } from '../../../../offer/model/OfferModel';
import { OrderModel } from '../../../model/OrderModel';
import { OfferAccordionCard } from '../../common/OfferCard/OfferAccordionCard';

import { OfferAccordionContent } from './components/OfferAccordionContent';
import { OfferActions } from './components/OfferActions';
import { OfferCardContextProvider } from './components/OfferCardContextProvider';
import { OfferSummary } from './components/OfferSummary';

// A draft is the customer's private composition and is not surfaced as a tag.
const ONBOARDING_TAG_LABEL_KEY: Record<Exclude<OnboardingState, OnboardingState.DRAFT>, TranslationKeyEn> = {
    [OnboardingState.REQUESTED]: 'supplierOnboarding.tag.requested',
    [OnboardingState.IN_PROGRESS]: 'supplierOnboarding.tag.inProgress',
    [OnboardingState.SUBMITTED]: 'supplierOnboarding.tag.submitted',
    [OnboardingState.CANCELED]: 'supplierOnboarding.tag.cancelled',
};

const OfferTagIndicators = styled.div`
    display: flex;
    background-color: ${theme.colors.background.inset};
    width: 100%;
    height: 35px;
    padding-left: ${theme.space[6]};
    padding-top: 5px;
    border-radius: ${theme.radii[8]} ${theme.radii[8]} 0 0;
    z-index: ${theme.zIndex[100]};
    position: absolute;
    top: 0;
    left: 0;
    column-gap: ${theme.space[4]};
`;

interface OfferCardProps {
    offer: OfferModel;
    order: OrderModel;
    isBestLiterPrice?: boolean;
    isRecommended?: boolean;
    isLastOpenOffer: boolean;
    isSpotAvailable: boolean;
    expanded: boolean;
    onClick: () => void;
    onboarding: SupplierOnboardingRequestMessage | null;
}

export const OfferCard = (props: OfferCardProps) => {
    const role = useRole();

    const onboarding = props.onboarding;
    // The onboarding is shared across a supplier's offers; only surface it on the one it was initiated on.
    const isOnboardingOffer = onboarding?.offerId === props.offer.id;

    const hasChangedItems = props.offer.items.some((item) => Object.values(item.changes).some((change) => change));
    const hasChangedSampleKits = props.offer.samplekits
        ? props.offer.samplekits?.some((sampleKit) => Object.values(sampleKit.changes).some((change) => change))
        : false;

    const hasChanges = hasChangedItems || hasChangedSampleKits;

    const showChangesTag = props.offer.displayQuoteDetails() && hasChanges;
    const showSupplierReadTag = role.isSuperAdmin() && props.offer.supplierRead;
    const showRecommendedTag = props.isRecommended;

    const showOnboardingTag = isOnboardingOffer && onboarding?.state !== OnboardingState.DRAFT;

    const showTags = showChangesTag || showSupplierReadTag || showRecommendedTag || showOnboardingTag;

    return (
        <OfferCardContextProvider
            offer={props.offer}
            order={props.order}
            isLastOpenOffer={props.isLastOpenOffer}
            isSpotAvailable={props.isSpotAvailable}
            onboarding={onboarding ?? undefined}
        >
            <Box paddingTop={showTags ? '30px' : 0} position="relative">
                {showTags ? (
                    <OfferTagIndicators>
                        {showRecommendedTag ? (
                            <Tag variant="special" emphasis Icon={IconSparkle}>
                                {translate('order.detail.offerCard.recommended')}
                            </Tag>
                        ) : null}
                        {showChangesTag ? (
                            <Tag variant="attention">{translate('order.detail.offerCard.changed')}</Tag>
                        ) : null}
                        {showSupplierReadTag ? (
                            <Tag variant="default">{translate('order.detail.offerCard.supplierRead')}</Tag>
                        ) : null}
                        {isOnboardingOffer && onboarding && onboarding.state !== OnboardingState.DRAFT ? (
                            <Tag variant="default">{translate(ONBOARDING_TAG_LABEL_KEY[onboarding.state])}</Tag>
                        ) : null}
                    </OfferTagIndicators>
                ) : null}

                <OfferAccordionCard expanded={props.expanded} accordionContent={<OfferAccordionContent />}>
                    <OfferSummary
                        onClick={props.onClick}
                        isBestLiterPrice={props.isBestLiterPrice}
                        isRecommended={props.isRecommended}
                    />
                    <OfferActions />
                </OfferAccordionCard>
            </Box>
        </OfferCardContextProvider>
    );
};
